import { useCallback, useEffect, useState, useContext, useRef } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import ReportsLayout from "./ReportsLayout";
import { useParams } from "react-router-dom";
import { FilterContext } from "../FilterContextProvider";
import Select from "react-select";
import useApi from "../Api/Api";
import ReactToPrint from "react-to-print";
import { exportToCSV } from "../Utility/Csv";
import Button from "../Components/Button";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS } from "chart.js/auto"; // eslint-disable-line

const ProductTotalsSales = () => {
  const { filterData } = useContext(FilterContext);
  const api = useApi();
  const printRef = useRef();
  const params = useParams();
  const [sales, setSales] = useState([]);
  const [selectedView, setSelectedView] = useState("number_sold");

  const getSales = useCallback(() => {
    let queryParams = {};
    if (params.productId) {
      queryParams.query = params.productId;
    }
    const queryString = Object.keys(queryParams)
      .map((param) => `${queryParams[param]}`)
      .join("&");
    filterData.artist_id &&
      api
        .get(`/sales/graphall?artist_id=${filterData.artist_id}&${queryString}`)
        .then((r) => {
          setSales(r.data);
        });
  }, [api, filterData.artist_id, params]);

  useEffect(() => {
    getSales();
  }, [getSales, params.productId]);

  const showSales = sales
    .filter((each) => each.date < filterData.end_date)
    .filter((each) => each.date > filterData.start_date);

  showSales.forEach((sale) => {
    sale.sold =
      parseFloat(sale.count_in) +
      parseFloat(sale.add) +
      parseFloat(sale.add_2) +
      parseFloat(sale.add_3) +
      parseFloat(sale.add_4) -
      sale.count_out -
      sale.damage_quantity -
      sale.band_comp_quantity -
      sale.ctm_comp -
      sale.management_comp -
      sale.discount_quantity;
  });

  const showDates = [
    ...new Set(
      showSales.map(
        (sale) =>
          `${sale.date.split("T")[0]}*${sale.event.city}*${sale.event.state}*${
            sale.event.attendance
          }*${sale.price}`
      )
    ),
  ];

  const showDatesArray = showDates.map(function (value) {
    return {
      label: `${value.split("*")[0]} ${value.split("*")[1]}, ${
        value.split("*")[2]
      } $${value.split("*")[4]}`,
      date: value.split("*")[0],
      city: value.split("*")[1],
      state: value.split("*")[2],
      attendance: value.split("*")[3],
      price: value.split("*")[4],
      sold: showSales
        .filter((one) => one.date.split("T")[0] === value.split("*")[0])
        .reduce((a, b) => {
          return a + b.sold;
        }, 0),
    };
  });

  const totalSold = showSales.reduce((a, b) => {
    return a + b.sold;
  }, 0);

  const grossSales = showSales.reduce((a, b) => {
    return a + b.sold * b.price;
  }, 0);

  const viewOptions = [
    {
      label: "Number sold",
      value: "number_sold",
    },
    {
      label: "Per head",
      value: "per_head",
    },
  ];

  const handleSelectedView = (option) => {
    setSelectedView(option.value);
  };

  let dataToShow = [];

  if (selectedView === "number_sold") {
    dataToShow = showDatesArray.map((each) => each.sold);
  } else if (selectedView === "per_head") {
    dataToShow = showDatesArray.map((each) => each.sold / each.attendance);
  }

  const dataToExport = showDatesArray.map((each) => ({
    Date: each.date,
    City: each.city,
    State: each.state,
    Number_sold: each.sold,
    Per_head: (each.sold / each.attendance),
    Price: each.price,
  }));

  return (
    <ReportsLayout>
      <div className="text-black bg-gray-100 pt-2">
        <div className="mt-8 mx-8 text-black bg-white rounded-lg border">
          <div className="mx-12 flex justify-end text-sm pt-10"></div>
          {showSales.length > 0 && (
            <div className="flex justify-end text-sm mr-12 -mt-2">
              <ReactToPrint
                trigger={() => (
                  <Button className="button" text="Print"></Button>
                )}
                content={() => printRef.current}
              />
              <Button
                className="button button-white ml-2"
                text="Export"
                onClick={() =>
                  exportToCSV(
                    dataToExport,
                    `${filterData.artist_name}_${sales[0]?.product?.name}_sales_performance`
                  )
                }
              ></Button>
            </div>
          )}
          <div className="mx-12 -mt-4 font-medium text-black text-sm">
            <Link to="/reports/producttotals">
              <FontAwesomeIcon icon={faArrowLeft}></FontAwesomeIcon> Back to all
              products
            </Link>
          </div>
          <div ref={printRef}>
            <div className="py-2 my-4 mx-12 text-2xl font-medium">
              <div>{sales[0]?.product?.name} totals sales performance</div>
              <div className="text-lg my-4 flex">
                <div className="w-1/4 mr-4">
                  Number sold: {totalSold?.toLocaleString()}
                </div>
                <div className="w-1/4 mr-4">
                  Gross sales:{" "}
                  {grossSales?.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </div>
                <div className="flex w-1/2">
                  <div>View by:</div>
                  <div className="w-2/3 ml-2">
                    <Select
                      id="sort_filter"
                      className="text-xs font-light w-full"
                      placeholder="Select forecast variable"
                      options={viewOptions}
                      onChange={handleSelectedView}
                      value={viewOptions?.filter(
                        (each) => each.value === selectedView
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div>
              {showDatesArray.length > 0 && (
                <>
                  <Line
                    data={{
                      labels: showDatesArray.map((each) => each.label),
                      datasets: [
                        {
                          data: dataToShow,
                          borderColor: "#3b82f6",
                        },
                      ],
                    }}
                    options={{
                      plugins: {
                        legend: {
                          display: false,
                        },
                      },
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </ReportsLayout>
  );
};

export default ProductTotalsSales;
